import { css, html, LitElement, nothing, type PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { consume } from '@lit/context';
import { inputResponseContext } from '../../contexts/inputResponse';
import {
    AGENT_PORTAL_DASH_URL,
    BackendFormResponse,
    convertDeliveryMethod,
    Producer,
    submitBlueAdvantageQuoteRequest,
    toApiDateFormat,
} from '../../util/api';
import { breakpointContext } from '../../contexts/breakpoint';
import { BREAKPOINT } from '../../util/breakpoints';
import { EVENT, makeCustomEvent } from '../../util/events';
import { PAGE } from '../../util/page';
import {
    getEmailValidationError,
    getNameValidationError,
    getPhoneValidationError,
    getZipValidationError,
    isValidZipcode,
} from '../../util/validation';
import { getCountyCode } from '../../util/county';
import { type CountyOption, getCountyOptionsByZipcode } from '../../util/zipcode';
import { fetchAgentDetails, type ProducerSessionDetails } from '../../util/producer-session';
import { FormValidationMessages } from '../../enums/textConstantEnums';
import { IS_AEP_SELECTION_WINDOW, IS_EFFECTIVE_PLAN_YEAR_EDITABLE } from '../../util/config';
import { sourceContext } from '../../contexts/source';
import { SOURCE } from '../../util/source';
import { submitInputs } from '../../controllers/inputs';
import { type ApplicantFormData, transformApplicantFormDataToRateQuoteFormData } from '../../util/form';
import { agentContext } from '../../contexts/agent';

interface FormValidationErrors {
    firstNameError: string;
    lastNameError: string;
    emailError: string;
    phoneError: string;
    zipCodeError: string;
    countyError: string;
    spouseFirstNameError: string;
    spouseLastNameError: string;
    householdIncomeError: string;
}

const FORM_DATA_SESSION_KEY = 'applicantFormData';
const MAX_CHILDREN = 8;
const ZIP_NUMERIC_ONLY_ERROR_MESSAGE = 'Zip code can only contain numbers';
const INVALID_ZIP_CODE_ERROR_MESSAGE = 'Invalid Zip Code';
const CHILD_DOB_MAX_AGE_ERROR_MESSAGE = 'Child age must be 26 or younger';
const APPLICANT_DOB_MIN_AGE_ERROR_MESSAGE = 'Date of birth must be within the last 120 years';
const DOB_FUTURE_DATE_ERROR_MESSAGE = 'Date of birth cannot be in the future.';

@customElement('rq-applicant-information')
export class ApplicantInformationPage extends LitElement {
    static styles = css`
        :host {
            display: block;
            width: 100%;
            min-height: 100vh;
            padding-top: 24px;
            padding-bottom: 24px;
            background: var(--lh-color-brand-dogwood, #f5f7f8);
        }

        lh-card {
            margin: 0 auto 24px;
            max-width: 900px;
        }

        lh-grid {
            margin: 24px 0;
        }

        lh-divider {
            margin: 32px 0;
        }

        lh-checkbox {
            margin: 16px 0;
            display: block;
        }

        lh-typography {
            margin-bottom: 16px;
        }

        .section-title {
            margin-top: 32px;
            margin-bottom: 16px;
        }

        .overlay {
            position: fixed;
            inset: 0;
            background: rgba(255, 255, 255, 0.6);
            backdrop-filter: blur(2px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
        }

        /* Visual fallback so Continue always looks and behaves disabled, regardless of lh-button internals. */
        .visually-disabled {
            opacity: 0.5;
            pointer-events: none;
        }
    `;

    @consume({ context: inputResponseContext, subscribe: true })
    @state()
    inputResponse!: BackendFormResponse;

    @consume({ context: breakpointContext, subscribe: true })
    breakpoint!: BREAKPOINT;

    @consume({ context: sourceContext, subscribe: true })
    source?: SOURCE;

    @consume({ context: agentContext, subscribe: true })
    agent?: Producer;

    @state()
    private producerSession?: ProducerSessionDetails;

    @state()
    private formData: ApplicantFormData = {
        deliveryMethod: '',
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        zipCode: '',
        county: '',
        email: '',
        phone: '',
        includeSpouse: false,
        spouseFirstName: '',
        spouseLastName: '',
        spouseDateOfBirth: '',
        includeChildren: false,
        numberOfChildren: 0,
        childrenDatesOfBirth: [],
        householdIncome: '',
        numberOfPeopleInHousehold: 0,
    };

    @state()
    private validationErrors: FormValidationErrors = {
        firstNameError: '',
        lastNameError: '',
        emailError: '',
        phoneError: '',
        zipCodeError: '',
        countyError: '',
        spouseFirstNameError: '',
        spouseLastNameError: '',
        householdIncomeError: '',
    };

    @state()
    private countyOptions: CountyOption[] = [];

    @state()
    private countyLoading = false;

    @state()
    private applicantState = '';

    @state()
    private countyLastZipLookup = '';

    @state()
    private loading = false;

    @state()
    private submitError = '';

    @state()
    private submitErrors: string[] = [];

    @state()
    private showChildrenAssistanceMessage = false;

    @state()
    private aepReasonSelection: '1' | '2' | '' = '';

    @state()
    private isApplicantDobEditing = false;

    @state()
    private isSpouseDobEditing = false;

    @state()
    private isPhoneEditing = false;

    @state()
    private isZipEditing = false;

    // @state()
    // private effectivePlanYear = DEFAULT_EFFECTIVE_PLAN_YEAR;

    // private get isAnnualEnrollmentPeriod(): boolean {
    //     return IS_EFFECTIVE_PLAN_YEAR_EDITABLE;
    // }

    override connectedCallback() {
        super.connectedCallback();
        this.restoreFormDataFromSession();
        this.loadProducerSession();
    }

    override updated(changedProperties: PropertyValues<this>) {
        super.updated(changedProperties);

        const hasRelevantChanges =
            changedProperties.has('formData' as keyof ApplicantInformationPage) ||
            changedProperties.has('inputResponse' as keyof ApplicantInformationPage);
        if (!hasRelevantChanges) {
            return;
        }

        const zipCode = this.formData.zipCode;
        if (
            isValidZipcode(zipCode) &&
            this.countyOptions.length === 0 &&
            !this.countyLoading &&
            this.countyLastZipLookup !== zipCode
        ) {
            this.countyLastZipLookup = zipCode;
            void this.lookupCountyByZipcode(zipCode);
        }
    }

    private loadProducerSession = async () => {
        if (this.source === SOURCE.EMBEDDED_AGENT && this.agent) {
            this.producerSession = this.agent;
        } else {
            try {
                this.producerSession = await fetchAgentDetails();
            } catch (error) {
                console.warn('Failed to load producer session:', error);
            }
        }

    };

    private restoreFormDataFromSession = () => {
        try {
            const savedData = sessionStorage.getItem(FORM_DATA_SESSION_KEY);
            if (savedData) {
                const parsed = JSON.parse(savedData);
                const restoredAepReason = parsed?.aepReasonSelection;
                this.formData = {
                    ...this.formData,
                    ...parsed,
                };

                this.aepReasonSelection =
                    restoredAepReason === '1' || restoredAepReason === '2' ? restoredAepReason : '';

                const restoredZip = String(parsed.zipCode || '')
                    .replace(/\D/g, '')
                    .slice(0, 5);
                if (isValidZipcode(restoredZip)) {
                    this.lookupCountyByZipcode(restoredZip);
                }
            }
        } catch (error) {
            // Silently fail if session data is corrupted
            console.warn('Failed to restore form data from session:', error);
        }
    };

    private saveFormDataToSession = () => {
        try {
            sessionStorage.setItem(
                FORM_DATA_SESSION_KEY,
                JSON.stringify({
                    ...this.formData,
                    aepReasonSelection: this.aepReasonSelection,
                })
            );
        } catch (error) {
            console.warn('Failed to save form data to session:', error);
        }
    };

    private onDeliveryMethodChange = (e: Event) => {
        const target = e.target as HTMLInputElement & { value: string };
        this.formData = {
            ...this.formData,
            deliveryMethod: target.value as 'pabol' | 'email',
        };
    };

    private onAepReasonChange = (e: Event) => {
        const eventWithDetail = e as Event & { detail?: { value?: string } };
        const target = e.target as HTMLInputElement & { value?: string };
        const nextValue = eventWithDetail.detail?.value || target.value || '';
        this.aepReasonSelection = nextValue === '2' ? '2' : nextValue === '1' ? '1' : '';
    };

    // private onEffectivePlanYearChange = (e: Event) => {
    //     const eventWithDetail = e as Event & { detail?: { value?: string } };
    //     const target = e.target as HTMLInputElement & { value?: string };
    //     const nextValue = eventWithDetail.detail?.value || target.value || '';
    //     this.effectivePlanYear = nextValue;
    // };

    private onInputChange = (e: Event, field: keyof ApplicantFormData) => {
        const target = e.target as HTMLInputElement;
        const eventWithDetail = e as Event & { detail?: { value?: unknown } };

        if (field === 'dateOfBirth') {
            this.isApplicantDobEditing =
                e.type === 'lh-input' || e.type === 'input' || e.type === 'lh-change' || e.type === 'change';
        }

        if (field === 'spouseDateOfBirth') {
            this.isSpouseDobEditing =
                e.type === 'lh-input' || e.type === 'input' || e.type === 'lh-change' || e.type === 'change';
        }

        if (field === 'phone') {
            this.isPhoneEditing = e.type === 'lh-input' || e.type === 'input';
            if (e.type === 'lh-change' || e.type === 'change') {
                this.isPhoneEditing = false;
            }
        }

        if (field === 'zipCode') {
            this.isZipEditing = e.type === 'lh-input' || e.type === 'input';
            if (e.type === 'lh-change' || e.type === 'change') {
                this.isZipEditing = false;
            }
        }

        // The lh-input wrapper can filter or omit invalid characters from detail.value
        // (e.g. pattern mismatches), so fall back to reading the underlying native input directly.
        const nativeInputValue = (target as unknown as { shadowRoot?: ShadowRoot }).shadowRoot?.querySelector(
            'input'
        )?.value;
        const rawInputValue =
            typeof eventWithDetail.detail?.value === 'string'
                ? eventWithDetail.detail.value
                : typeof nativeInputValue === 'string'
                  ? nativeInputValue
                  : String(target.value ?? '');
        let value: string | number = target.type === 'number' ? Number(rawInputValue) : rawInputValue;

        if (field === 'dateOfBirth' || field === 'spouseDateOfBirth') {
            // Keep native date input format (YYYY-MM-DD).
            value = String(rawInputValue || '').trim();
        }

        if (field === 'zipCode') {
            // Only allow numeric ZIP input and cap at 5 digits.
            value = String(rawInputValue || '')
                .replace(/\D/g, '')
                .slice(0, 5);
            // Keep the displayed value in sync with sanitized digits only.
            if (rawInputValue !== value) {
                (target as unknown as { change?: (value: string) => void }).change?.(String(value));
            }
        }

        if (field === 'numberOfChildren') {
            const numberOfChildren = Number.isNaN(Number(value)) ? 0 : Number(value);
            this.showChildrenAssistanceMessage = numberOfChildren > MAX_CHILDREN;
            value = Math.max(0, Math.min(numberOfChildren, MAX_CHILDREN));
        }

        if (field === 'householdIncome') {
            // Strip non-digits and cap at 10 characters
            const raw = rawInputValue;
            const stripped = raw.replace(/\D/g, '').slice(0, 10);
            value = stripped;
            this.validationErrors = {
                ...this.validationErrors,
                householdIncomeError: raw && /[^\d]/.test(raw) ? FormValidationMessages.NumbersOnly : '',
            };
            // Force a render pass immediately so Continue's disabled state can't lag behind.
            this.requestUpdate();
        }

        // Update formData
        this.formData = {
            ...this.formData,
            [field]: value,
        };

        // householdIncome error was already derived from the raw (unstripped) input above;
        // re-validating here with the stripped value would incorrectly clear it.
        if (field === 'zipCode') {
            // Validate against the raw value so we can show numbers-only errors.
            this.updateFieldValidation('zipCode', rawInputValue);
        } else if (field !== 'householdIncome') {
            this.updateFieldValidation(field as string, value);
        }
    };

    private onZipFocusOut = () => {
        // Ensure ZIP errors are re-shown after blur even when value did not change,
        // because some lh-input change events do not fire for same-value commits.
        this.isZipEditing = false;
        this.updateFieldValidation('zipCode', this.formData.zipCode);
    };

    private onApplicantDobFocusOut = () => {
        this.isApplicantDobEditing = false;
    };

    private onSpouseDobFocusOut = () => {
        this.isSpouseDobEditing = false;
    };

    private updateFieldValidation = (field: string, value: unknown) => {
        const stringValue = String(value || '');

        switch (field) {
            case 'firstName':
                this.validationErrors = {
                    ...this.validationErrors,
                    firstNameError: getNameValidationError(stringValue),
                };
                break;
            case 'lastName':
                this.validationErrors = {
                    ...this.validationErrors,
                    lastNameError: getNameValidationError(stringValue),
                };
                break;
            case 'email':
                this.validationErrors = {
                    ...this.validationErrors,
                    emailError: getEmailValidationError(stringValue),
                };
                break;
            case 'phone':
                this.validationErrors = {
                    ...this.validationErrors,
                    phoneError: getPhoneValidationError(stringValue),
                };
                break;
            case 'zipCode': {
                // Remove non-digits and limit to 5 characters
                const cleanZip = stringValue.replace(/\D/g, '').slice(0, 5);
                const hasNonNumericZipInput = /[^\d\s-]/.test(stringValue);
                this.formData = { ...this.formData, zipCode: cleanZip };
                this.validationErrors = {
                    ...this.validationErrors,
                    zipCodeError: hasNonNumericZipInput
                        ? ZIP_NUMERIC_ONLY_ERROR_MESSAGE
                        : getZipValidationError(cleanZip),
                };
                // If zipcode is valid, look up the county
                if (isValidZipcode(cleanZip)) {
                    this.countyLastZipLookup = cleanZip;
                    this.lookupCountyByZipcode(cleanZip);
                } else {
                    // Clear county if zipcode becomes invalid
                    this.formData = { ...this.formData, county: '' };
                    this.countyOptions = [];
                    this.countyLastZipLookup = '';
                }
                break;
            }
            case 'county':
                // County selection from dropdown
                if (stringValue) {
                    this.validationErrors = {
                        ...this.validationErrors,
                        countyError: '',
                    };
                }
                break;
            case 'spouseFirstName':
                this.validationErrors = {
                    ...this.validationErrors,
                    spouseFirstNameError: getNameValidationError(stringValue),
                };
                break;
            case 'spouseLastName':
                this.validationErrors = {
                    ...this.validationErrors,
                    spouseLastNameError: getNameValidationError(stringValue),
                };
                break;
        }
    };

    private lookupCountyByZipcode = async (zipcode: string) => {
        this.countyLastZipLookup = zipcode;
        this.countyLoading = true;
        try {
            const allCountyOptions = await getCountyOptionsByZipcode(zipcode);
            const detectedState = allCountyOptions[0]?.state_abbreviation || '';
            const ncCountyOptions = allCountyOptions.filter(
                (option) => option.state_abbreviation.toLowerCase() === 'nc'
            );

            this.applicantState = detectedState;
            this.countyOptions = ncCountyOptions;
            const existingCounty = this.formData.county;

            if (ncCountyOptions.length > 0) {
                this.validationErrors = {
                    ...this.validationErrors,
                    zipCodeError: '',
                    countyError: '',
                };

                if (ncCountyOptions.length === 1) {
                    const county = ncCountyOptions[0].county_name;
                    this.formData = { ...this.formData, county };
                } else {
                    const hasExistingCounty = ncCountyOptions.some((option) => option.county_name === existingCounty);
                    this.formData = {
                        ...this.formData,
                        county: hasExistingCounty ? existingCounty : '',
                    };
                }
                this.requestUpdate();
            } else {
                this.formData = { ...this.formData, county: '' };
                if (detectedState && detectedState.toLowerCase() !== 'nc') {
                    this.validationErrors = {
                        ...this.validationErrors,
                        zipCodeError: 'Enter valid NC zip code.',
                        countyError: 'This ZIP Code is not in North Carolina.',
                    };
                } else {
                    this.validationErrors = {
                        ...this.validationErrors,
                        zipCodeError: INVALID_ZIP_CODE_ERROR_MESSAGE,
                        countyError: '',
                    };
                }
            }
        } catch {
            this.formData = { ...this.formData, county: '' };
            this.countyOptions = [];
            this.applicantState = '';
            this.validationErrors = {
                ...this.validationErrors,
                countyError: 'Unable to validate ZIP Code right now. Please try again.',
            };
        } finally {
            this.countyLoading = false;
        }
    };

    private isFormValid = (): boolean => {
        if (!this.formData.deliveryMethod) return false;
        if (!this.formData.firstName || this.validationErrors.firstNameError) return false;
        if (!this.formData.lastName || this.validationErrors.lastNameError) return false;
        if (!this.isDobValid(this.formData.dateOfBirth)) return false;
        if (this.validationErrors.zipCodeError) return false;
        if (!this.formData.zipCode || !isValidZipcode(this.formData.zipCode)) return false;
        if (!this.formData.county) return false;
        if (this.applicantState && this.applicantState.toLowerCase() !== 'nc') return false;
        // Email is required only when delivery method is 'email' (Applicant submitted)
        if (this.isEmailRequired && (!this.formData.email || this.validationErrors.emailError)) return false;
        if (this.validationErrors.emailError) return false;

        // Phone is optional but if provided must be valid
        if (this.formData.phone && this.validationErrors.phoneError) return false;

        // Spouse validations
        if (this.formData.includeSpouse) {
            if (!this.formData.spouseFirstName || this.validationErrors.spouseFirstNameError) return false;
            if (!this.formData.spouseLastName || this.validationErrors.spouseLastNameError) return false;
            if (!this.isDobValid(this.formData.spouseDateOfBirth)) return false;
        }

        // Children validations
        if (this.formData.includeChildren) {
            if (this.formData.numberOfChildren < 1) return false;
            const childrenToValidate = Math.min(this.formData.numberOfChildren, MAX_CHILDREN);
            for (let i = 0; i < childrenToValidate; i++) {
                const childDob = this.formData.childrenDatesOfBirth[i];
                if (!this.isDobValid(childDob) || !this.isChildDobValid(childDob)) return false;
            }
        }

        return true;
    };

    private get minDobDate(): string {
        const d = new Date();
        d.setFullYear(d.getFullYear() - 120);
        return d.toISOString().split('T')[0];
    }

    private parseDateInput(value: string): Date | null {
        const trimmed = value.trim();
        const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!isoMatch) return null;

        const yyyy = Number(isoMatch[1]);
        const mm = Number(isoMatch[2]);
        const dd = Number(isoMatch[3]);

        const parsed = new Date(yyyy, mm - 1, dd);
        if (parsed.getFullYear() !== yyyy || parsed.getMonth() !== mm - 1 || parsed.getDate() !== dd) {
            return null;
        }

        parsed.setHours(0, 0, 0, 0);
        return parsed;
    }

    private isDobValid(dob: string): boolean {
        const parsedDob = this.parseDateInput(dob);
        if (!parsedDob) return false;

        const minDob = new Date();
        minDob.setHours(0, 0, 0, 0);
        minDob.setFullYear(minDob.getFullYear() - 120);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return parsedDob >= minDob && parsedDob <= today;
    }

    private isChildDobValid(dob: string): boolean {
        if (!this.isDobValid(dob)) return false;

        const maxAgeDob = new Date();
        maxAgeDob.setFullYear(maxAgeDob.getFullYear() - 26);
        const maxAgeDobIso = maxAgeDob.toISOString().split('T')[0];

        // Child DOB must be within the last 26 years (inclusive).
        return dob >= maxAgeDobIso;
    }

    private getChildDobError(dob: string): string {
        if (!dob || !this.isDobValid(dob)) return '';
        return this.isChildDobValid(dob) ? '' : CHILD_DOB_MAX_AGE_ERROR_MESSAGE;
    }

    private getDobErrorMessage(dob: string, isEditing: boolean): string {
        if (!dob) return '';
        if (isEditing) return '';

        const parsedDob = this.parseDateInput(dob);
        if (!parsedDob) return APPLICANT_DOB_MIN_AGE_ERROR_MESSAGE;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (parsedDob > today) return DOB_FUTURE_DATE_ERROR_MESSAGE;

        return this.isDobValid(dob) ? '' : APPLICANT_DOB_MIN_AGE_ERROR_MESSAGE;
    }

    private getApplicantDobError(): string {
        return this.getDobErrorMessage(this.formData.dateOfBirth, this.isApplicantDobEditing);
    }

    private getSpouseDobError(): string {
        return this.getDobErrorMessage(this.formData.spouseDateOfBirth, this.isSpouseDobEditing);
    }

    private get zipCodeErrorForDisplay(): string {
        return this.isZipEditing ? '' : this.validationErrors.zipCodeError;
    }

    private get phoneErrorForDisplay(): string {
        return this.isPhoneEditing ? '' : this.validationErrors.phoneError;
    }

    private get isEmailRequired(): boolean {
        return this.formData.deliveryMethod === 'email';
    }

    private get isContinueDisabled(): boolean {
        // Household income is optional; only validate format when a value has been entered.
        const isHouseholdIncomeValid = !this.formData.householdIncome || /^\d+$/.test(this.formData.householdIncome);
        const requiredChildDOBs = this.formData.includeChildren
            ? Math.min(this.formData.numberOfChildren, MAX_CHILDREN)
            : 0;
        const childDOBsFilled =
            requiredChildDOBs === 0 ||
            (this.formData.childrenDatesOfBirth.slice(0, requiredChildDOBs).length === requiredChildDOBs &&
                this.formData.childrenDatesOfBirth.slice(0, requiredChildDOBs).every((dob) => !!dob));
        const childDOBsAgeValid =
            requiredChildDOBs === 0 ||
            this.formData.childrenDatesOfBirth
                .slice(0, requiredChildDOBs)
                .every((dob) => this.isDobValid(dob) && this.isChildDobValid(dob));

        return (
            (IS_AEP_SELECTION_WINDOW && !this.aepReasonSelection) ||
            !this.formData.deliveryMethod ||
            !this.formData.firstName ||
            !!this.validationErrors.firstNameError ||
            !this.formData.lastName ||
            !!this.validationErrors.lastNameError ||
            !this.isDobValid(this.formData.dateOfBirth) ||
            !isValidZipcode(this.formData.zipCode) ||
            !!this.validationErrors.zipCodeError ||
            !this.formData.county ||
            !!this.validationErrors.countyError ||
            (!!this.applicantState && this.applicantState.toLowerCase() !== 'nc') ||
            this.countyLoading ||
            (this.isEmailRequired && !this.formData.email) ||
            !!this.validationErrors.emailError ||
            !!this.validationErrors.phoneError ||
            (this.formData.includeSpouse &&
                (!this.formData.spouseFirstName ||
                    !!this.validationErrors.spouseFirstNameError ||
                    !this.formData.spouseLastName ||
                    !!this.validationErrors.spouseLastNameError ||
                    !this.isDobValid(this.formData.spouseDateOfBirth))) ||
            (this.formData.includeChildren &&
                (this.formData.numberOfChildren < 1 || !childDOBsFilled || !childDOBsAgeValid)) ||
            !isHouseholdIncomeValid ||
            !!this.validationErrors.householdIncomeError ||
            this.loading
        );
    }

    private onCheckboxChange = (e: Event, field: 'includeSpouse' | 'includeChildren') => {
        const target = e.target as HTMLInputElement;
        const isChecked = target.checked;

        if (field === 'includeSpouse' && !isChecked) {
            this.formData = {
                ...this.formData,
                includeSpouse: false,
                spouseFirstName: '',
                spouseLastName: '',
                spouseDateOfBirth: '',
            };
            this.validationErrors = {
                ...this.validationErrors,
                spouseFirstNameError: '',
                spouseLastNameError: '',
            };
            return;
        }

        if (field === 'includeChildren' && !isChecked) {
            this.formData = {
                ...this.formData,
                includeChildren: false,
                numberOfChildren: 0,
                childrenDatesOfBirth: [],
            };
            this.showChildrenAssistanceMessage = false;
            return;
        }

        this.formData = {
            ...this.formData,
            [field]: isChecked,
        };
    };

    private onCancel = () => {
        window.location.href = AGENT_PORTAL_DASH_URL();
    };

    private onContinue = async () => {
        // Hard-stop guard: never proceed while any validation error blocks Continue.
        if (this.isContinueDisabled) {
            return;
        }

        // Validate all fields
        if (!this.isFormValid()) {
            // Trigger validation on all fields to show error messages
            this.updateFieldValidation('firstName', this.formData.firstName);
            this.updateFieldValidation('lastName', this.formData.lastName);
            this.updateFieldValidation('email', this.formData.email);
            this.updateFieldValidation('phone', this.formData.phone);
            this.updateFieldValidation('zipCode', this.formData.zipCode);

            if (this.formData.includeSpouse) {
                this.updateFieldValidation('spouseFirstName', this.formData.spouseFirstName);
                this.updateFieldValidation('spouseLastName', this.formData.spouseLastName);
            }

            return;
        }

        const enrollPeriod = IS_EFFECTIVE_PLAN_YEAR_EDITABLE && this.aepReasonSelection === '1' ? 'AEP' : 'SEP';
        const countyCode = getCountyCode(this.formData.county);
        this.loading = true;
        this.submitError = '';
        this.submitErrors = [];
        if (this.source === SOURCE.EMBEDDED_AGENT) {
            const rateQuoteFormData = transformApplicantFormDataToRateQuoteFormData(this.formData, {
                aepReasonSelection: this.aepReasonSelection,
                producerNumber: this.producerSession?.producerNumber,
                agentUserId: this.producerSession?.userID,
            });
            const data = await submitInputs(rateQuoteFormData, true);
            if (data) {
                data.applicants[0].firstName = this.formData.firstName;
                data.applicants[0].lastName = this.formData.lastName;
                if (this.formData.includeSpouse) {
                    data.applicants[1].firstName = this.formData.spouseFirstName;
                    data.applicants[1].lastName = this.formData.spouseLastName;
                }
                this.dispatchEvent(
                    makeCustomEvent(EVENT.UPDATE_RESPONSE, {
                        data,
                        formData: rateQuoteFormData,
                        deliveryMethod: this.formData.deliveryMethod,
                    })
                );
            }
            return;
        }
        try {
            const response = await submitBlueAdvantageQuoteRequest({
                processNow: true,
                productTypeName: 'BlueAdvantage',
                enrollPeriod,
                deliveryMethod: convertDeliveryMethod(this.formData.deliveryMethod),
                applicantFirstName: this.formData.firstName,
                applicantLastName: this.formData.lastName,
                applicantBirthdate: toApiDateFormat(this.formData.dateOfBirth),
                applicantZipCode: this.formData.zipCode,
                countyName: this.formData.county,
                applicantCountyCode: countyCode,
                applicantEMail: this.formData.email,
                applicantPhone: this.formData.phone,
                spouseIncluded: this.formData.includeSpouse,
                spouseFirstName: this.formData.spouseFirstName || undefined,
                spouseLastName: this.formData.spouseLastName || undefined,
                spouseBirthdate: this.formData.spouseDateOfBirth
                    ? toApiDateFormat(this.formData.spouseDateOfBirth)
                    : undefined,
                dependentsIncluded: this.formData.includeChildren && this.formData.numberOfChildren > 0,
                numberDependents: this.formData.includeChildren ? this.formData.numberOfChildren : 0,
                dependents: this.formData.includeChildren
                    ? this.formData.childrenDatesOfBirth
                          .slice(0, Math.min(this.formData.numberOfChildren, MAX_CHILDREN))
                          .map((dob) => ({ dob: toApiDateFormat(dob) }))
                    : [],
                householdIncome: this.formData.householdIncome,
                noOfPeopleInHouseHold: this.formData.numberOfPeopleInHousehold,
            });

            if (!response.ok) {
                // Handle 401 Unauthorized - likely need to log in first
                if (response.status === 401) {
                    this.submitError =
                        'Authentication required. Please log in to agentps.bcbsnc.com first, then try again.';
                } else {
                    this.submitError = `Request failed: ${response.statusText || response.status}`;
                }
                return;
            }

            const html = await response.text();
            const errors = this.parseResponseErrors(html);

            if (errors.length > 0) {
                this.submitErrors = errors;
                this.scrollToTop();
                return;
            }

            const rateObj = this.parseRateObj(html);
            const responseQuoteId = rateObj?.quoteId || '';

            if (responseQuoteId) {
                sessionStorage.setItem('embeddedQuoteId', responseQuoteId);
            }

            // Save form data to session before navigating
            this.saveFormDataToSession();

            // Update URL to plan results page before navigating
            const baseUrl = window.location.origin + (import.meta.env.BASE_URL || '/get-a-quote/');
            const urlParams = new URLSearchParams(window.location.search);
            const userID = urlParams.get('userID');
            const queryParams = userID
                ? `?psale=y&productTypeName=BlueAdvantage&userID=${userID}`
                : '?psale=y&productTypeName=BlueAdvantage';
            window.history.pushState({}, '', baseUrl + 'plans' + queryParams);

            if (rateObj) {
                this.dispatchEvent(
                    makeCustomEvent(EVENT.UPDATE_RESPONSE, {
                        data: {
                            ...rateObj,
                            quoteId: responseQuoteId || rateObj.quoteId,
                        },
                    })
                );
            } else {
                this.dispatchEvent(
                    makeCustomEvent(EVENT.CHANGE_PAGE, {
                        page: PAGE.RESULTS,
                    })
                );
            }
        } catch (err) {
            this.submitError = err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.';
        } finally {
            this.loading = false;
        }
    };

    private parseResponseErrors = (responseHtml: string): string[] => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(responseHtml, 'text/html');
        const errorList = doc.querySelector('span.rq-error ul.error');
        if (!errorList) return [];

        return Array.from(errorList.querySelectorAll('li')).map((li) => li.innerHTML.trim());
    };

    private parseRateObj = (responseHtml: string): BackendFormResponse | null => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(responseHtml, 'text/html');
        const scripts = Array.from(doc.querySelectorAll('script'));

        for (const script of scripts) {
            const content = script.textContent || '';
            const startMarker = 'var rateObj = ';
            const startIdx = content.indexOf(startMarker);

            if (startIdx === -1) continue;

            const jsonStart = startIdx + startMarker.length;

            // Use bracket counting to find the end of the JSON object
            let depth = 0;
            let jsonEnd = -1;
            for (let i = jsonStart; i < content.length; i++) {
                if (content[i] === '{') depth++;
                else if (content[i] === '}') {
                    depth--;
                    if (depth === 0) {
                        jsonEnd = i + 1;
                        break;
                    }
                }
            }

            if (jsonEnd === -1) continue;

            try {
                return JSON.parse(content.slice(jsonStart, jsonEnd)) as BackendFormResponse;
            } catch {
                continue;
            }
        }

        return null;
    };

    private scrollToTop = () => {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    render() {
        return html`
            ${
                this.producerSession && (this.producerSession.firstName || this.producerSession.lastName)
                    ? html`
                          <lh-container spacing="sm" theme="neutral-light">
                              <lh-typography size="lg" weight="700">Agent information</lh-typography>
                              <lh-grid>
                                  <lh-grid-item>
                                      <lh-typography type="text" size="md">
                                          ${this.producerSession.lastName}, ${this.producerSession.firstName}:
                                          ${this.producerSession.producerNumber}
                                      </lh-typography>
                                  </lh-grid-item>
                                  <lh-grid-item>
                                      <lh-typography
                                          type="text"
                                          size="md"
                                          alignment="${this.breakpoint === BREAKPOINT.PHONE ? 'left' : 'right'}"
                                      >
                                          ${this.producerSession.phone} | ${this.producerSession.email}
                                      </lh-typography>
                                  </lh-grid-item>
                              </lh-grid>
                          </lh-container>
                      `
                    : nothing
            }
            <lh-container
                theme="neutral-light"
                gutters="none"
                spacing="none"
                content-width="md"
                padding-top="md"
                padding-bottom="md"
            >
                <div
                    style="background: white;padding: 32px;border-radius: var(--lh-radii-md, 12px);display: flex;flex-direction: column;gap: 24px;"
                >
                    <lh-container slot="content" theme="white" content-width="fluid">
                        <lh-typography type="heading" size="xl">Enter applicant information</lh-typography>
                        ${
                            this.submitErrors.length > 0
                                ? html`
                                      <lh-alert variant="error" inline spacing-top="sm">
                                          <ul style="margin: 0; padding-left: 1.25rem;">
                                              ${this.submitErrors.map((err) => html`<li .innerHTML=${err}></li>`)}
                                          </ul>
                                      </lh-alert>
                                  `
                                : this.submitError
                                  ? html`<lh-alert variant="error" inline spacing-top="sm"
                                        >${this.submitError}</lh-alert
                                    >`
                                  : nothing
                        }
                        <lh-typography size="lg" weight="700">Select quote delivery method</lh-typography>
                        <lh-radio-group
                            spacing-top="none"
                            name="delivery-method"
                            value=${this.formData.deliveryMethod}
                            default-value=${this.formData.deliveryMethod}
                            @lh-change=${this.onDeliveryMethodChange}
                        >
                            <lh-grid>
                                <lh-grid-item vertical-align="stretch">
                                    <lh-radio-card value="pabol">
                                        <lh-icon name="user-plus" slot="media" size="sm"></lh-icon>
                                        <div slot="content">
                                            <lh-typography weight="700">Agent submitted - PABOL</lh-typography>
                                            <lh-typography size="sm"
                                                >If you are meeting with your client in person, you may display the
                                                quote for your client's review and then proceed to apply
                                                online</lh-typography
                                            >
                                            <br />
                                        </div>
                                    </lh-radio-card>
                                </lh-grid-item>
                                <lh-grid-item vertical-align="stretch">
                                    <lh-radio-card value="email">
                                        <lh-icon name="envelope-simple" slot="media" size="sm"></lh-icon>
                                        <div slot="content">
                                            <lh-typography weight="700">Applicant submitted</lh-typography>
                                            <lh-typography size="sm"
                                                >If your client is at another location, you may e-mail the client a link
                                                to view the rate quote results. A client at another location may proceed
                                                to apply online at their convenience.</lh-typography
                                            >
                                        </div>
                                    </lh-radio-card>
                                </lh-grid-item>
                            </lh-grid>
                        </lh-radio-group>

                        <lh-divider></lh-divider>
                        ${
                            IS_AEP_SELECTION_WINDOW
                                ? html` <lh-typography size="lg" weight="700"
                                          >Reason for new quote
                                          <sup
                                              style="color:var(--lh-theme-color-content-error);font-weight:var(--lh-font-weight-700);font-family:var(--lh-font-family-sans-serif)"
                                              aria-hidden="true"
                                          >
                                              *
                                          </sup></lh-typography
                                      >
                                      <lh-radio-group
                                          required
                                          name="numbers"
                                          value=${this.aepReasonSelection}
                                          default-value=${this.aepReasonSelection}
                                          @lh-change=${this.onAepReasonChange}
                                      >
                                          <lh-radio value="1"
                                              >The applicant is shopping during Open Enrollment</lh-radio
                                          >
                                          <lh-radio value="2"
                                              >The applicant has a Qualifying Life Event (QLE) and may be eligible for
                                              earlier effective dates</lh-radio
                                          >
                                      </lh-radio-group>
                                      <lh-divider></lh-divider>`
                                : nothing
                        }
                        <lh-typography size="lg" weight="700">Applicant information</lh-typography>
                        <lh-grid gap="lg">
                            <lh-grid-item>
                                <lh-input
                                    name="firstName"
                                    required
                                    max-count="40"
                                    width="402px"
                                    value=${this.formData.firstName}
                                    default-value=${this.formData.firstName}
                                    error-message=${this.validationErrors.firstNameError}
                                    @lh-input=${(e: Event) => this.onInputChange(e, 'firstName')}
                                    >First name</lh-input
                                >
                            </lh-grid-item>
                            <lh-grid-item>
                                <lh-input
                                    name="lastName"
                                    required
                                    max-count="40"
                                    width="402px"
                                    value=${this.formData.lastName}
                                    default-value=${this.formData.lastName}
                                    error-message=${this.validationErrors.lastNameError}
                                    @lh-input=${(e: Event) => this.onInputChange(e, 'lastName')}
                                    >Last name</lh-input
                                >
                            </lh-grid-item>
                        </lh-grid>

                        <lh-grid gap="lg">
                            <lh-grid-item span="6">
                                <lh-input
                                    name="dateOfBirth"
                                    required
                                    type="date"
                                    width="402px"
                                    placeholder="MM/DD/YYYY"
                                    max=${new Date().toISOString().split('T')[0]}
                                    value=${this.formData.dateOfBirth}
                                    default-value=${this.formData.dateOfBirth}
                                    error-message=${this.getApplicantDobError()}
                                    @lh-input=${(e: Event) => this.onInputChange(e, 'dateOfBirth')}
                                    @lh-change=${(e: Event) => this.onInputChange(e, 'dateOfBirth')}
                                    @lh-blur=${this.onApplicantDobFocusOut}
                                    >Date of birth</lh-input
                                >
                            </lh-grid-item>
                        </lh-grid>

                        <lh-grid gap="lg">
                            <lh-grid-item>
                                <lh-input
                                    name="zipCode"
                                    required
                                    width="402px"
                                    max-count="5"
                                    value=${this.formData.zipCode}
                                    default-value=${this.formData.zipCode}
                                    error-message=${this.zipCodeErrorForDisplay}
                                    @lh-input=${(e: Event) => this.onInputChange(e, 'zipCode')}
                                    @lh-change=${(e: Event) => this.onInputChange(e, 'zipCode')}
                                    @lh-blur=${this.onZipFocusOut}
                                    >Zip code</lh-input
                                >
                            </lh-grid-item>
                            <lh-grid-item>
                                ${
                                    this.countyLoading
                                        ? html`
                                              <lh-container theme="transparent" content-width="fluid">
                                                  <lh-typography type="text" required>County</lh-typography>
                                                  <lh-loader size="sm"></lh-loader>
                                              </lh-container>
                                          `
                                        : this.countyOptions.length > 1
                                          ? html`
                                                <lh-select
                                                    ?required=${true}
                                                    name="county"
                                                    width="402px"
                                                    value=${this.formData.county}
                                                    error-message=${this.validationErrors.countyError}
                                                    @lh-change=${(e: Event) => this.onInputChange(e, 'county')}
                                                >
                                                    County
                                                    <lh-option
                                                        slot="options"
                                                        value=""
                                                        ?default-selected=${!this.formData.county}
                                                    >
                                                        Select one
                                                    </lh-option>
                                                    ${this.countyOptions.map(
                                                        (option) => html`
                                                            <lh-option
                                                                slot="options"
                                                                value=${option.county_name}
                                                                ?default-selected=${this.formData.county ===
                                                                option.county_name}
                                                            >
                                                                ${option.county_name}
                                                            </lh-option>
                                                        `
                                                    )}
                                                </lh-select>
                                            `
                                          : html`
                                                <lh-input
                                                    ?required=${true}
                                                    disabled
                                                    name="county"
                                                    readonly
                                                    width="402px"
                                                    default-value=${this.formData.county}
                                                    error-message=${this.validationErrors.countyError}
                                                >
                                                    County
                                                    <sup
                                                        style="color:var(--lh-theme-color-content-error);font-weight:var(--lh-font-weight-700);font-family:var(--lh-font-family-sans-serif);font-size:1rem;position:relative;top:4px"
                                                        aria-hidden="true"
                                                    >
                                                        *
                                                    </sup>
                                                </lh-input>
                                            `
                                }
                            </lh-grid-item>
                        </lh-grid>
                        <lh-grid gap="lg">
                            <lh-grid-item>
                                <lh-input
                                    name="email"
                                    ?required=${this.isEmailRequired}
                                    type="email"
                                    width="402px"
                                    value=${this.formData.email}
                                    default-value=${this.formData.email}
                                    error-message=${this.validationErrors.emailError}
                                    @lh-input=${(e: Event) => this.onInputChange(e, 'email')}
                                    >Email</lh-input
                                >
                            </lh-grid-item>
                            <lh-grid-item>
                                <lh-input
                                    name="phone"
                                    type="tel"
                                    width="402px"
                                    value=${this.formData.phone}
                                    default-value=${this.formData.phone}
                                    error-message=${this.phoneErrorForDisplay}
                                    @lh-input=${(e: Event) => this.onInputChange(e, 'phone')}
                                    @lh-change=${(e: Event) => this.onInputChange(e, 'phone')}
                                    >Phone</lh-input
                                >
                            </lh-grid-item>
                        </lh-grid>

                        <lh-checkbox
                            name="includeSpouse"
                            value="includeSpouse"
                            ?checked=${this.formData.includeSpouse}
                            ?default-checked=${this.formData.includeSpouse}
                            @lh-change=${(e: Event) => this.onCheckboxChange(e, 'includeSpouse')}
                        >
                            <strong>Include spouse or domestic partner on policy</strong>
                        </lh-checkbox>

                        ${
                            this.formData.includeSpouse
                                ? html`
                                      <lh-grid gap="lg">
                                          <lh-grid-item>
                                              <lh-input
                                                  name="spouseFirstName"
                                                  required
                                                  max-count="40"
                                                  width="402px"
                                                  value=${this.formData.spouseFirstName}
                                                  default-value=${this.formData.spouseFirstName}
                                                  error-message=${this.validationErrors.spouseFirstNameError}
                                                  @lh-input=${(e: Event) => this.onInputChange(e, 'spouseFirstName')}
                                                  >Spouse's first name</lh-input
                                              >
                                          </lh-grid-item>
                                          <lh-grid-item>
                                              <lh-input
                                                  name="spouseLastName"
                                                  required
                                                  max-count="40"
                                                  width="402px"
                                                  value=${this.formData.spouseLastName}
                                                  default-value=${this.formData.spouseLastName}
                                                  error-message=${this.validationErrors.spouseLastNameError}
                                                  @lh-input=${(e: Event) => this.onInputChange(e, 'spouseLastName')}
                                                  >Spouse's last name</lh-input
                                              >
                                          </lh-grid-item>
                                      </lh-grid>

                                      <lh-grid gap="lg">
                                          <lh-grid-item span="6">
                                              <lh-input
                                                  name="spouseDateOfBirth"
                                                  required
                                                  type="date"
                                                  width="402px"
                                                  placeholder="MM/DD/YYYY"
                                                  max=${new Date().toISOString().split('T')[0]}
                                                  value=${this.formData.spouseDateOfBirth}
                                                  default-value=${this.formData.spouseDateOfBirth}
                                                  error-message=${this.getSpouseDobError()}
                                                  @lh-input=${(e: Event) => this.onInputChange(e, 'spouseDateOfBirth')}
                                                  @lh-blur=${this.onSpouseDobFocusOut}
                                                  >Spouse's date of birth</lh-input
                                              >
                                          </lh-grid-item>
                                      </lh-grid>
                                  `
                                : nothing
                        }

                        <lh-checkbox
                            name="includeChildren"
                            value="includeChildren"
                            ?checked=${this.formData.includeChildren}
                            ?default-checked=${this.formData.includeChildren}
                            @lh-change=${(e: Event) => this.onCheckboxChange(e, 'includeChildren')}
                        >
                            <strong>Include children on policy</strong>
                        </lh-checkbox>

                        ${
                            this.formData.includeChildren
                                ? html`
                                      <lh-grid gap="lg">
                                          <lh-grid-item span="6">
                                              <lh-input
                                                  name="numberOfChildren"
                                                  placeholder="0"
                                                  required
                                                  type="number"
                                                  min="0"
                                                  max="8"
                                                  width="402px"
                                                  value=${this.formData.numberOfChildren}
                                                  default-value=${this.formData.numberOfChildren}
                                                  @lh-input=${(e: Event) => this.onInputChange(e, 'numberOfChildren')}
                                                  >Number of children</lh-input
                                              >
                                              ${this.showChildrenAssistanceMessage
                                                  ? html`
                                                        <lh-typography size="xs" color="subtle" spacing-top="lg">
                                                            If you need to add more than 8 children, please call for
                                                            assistance. 1-800-894-2880
                                                        </lh-typography>
                                                    `
                                                  : nothing}
                                          </lh-grid-item>
                                      </lh-grid>

                                      ${this.formData.numberOfChildren > 0
                                          ? html`
                                                ${Array.from({
                                                    length: Math.min(this.formData.numberOfChildren, MAX_CHILDREN),
                                                }).map(
                                                    (_, index) => html`
                                                        <lh-grid gap="lg">
                                                            <lh-grid-item span="6">
                                                                <lh-input
                                                                    name="child${index}DateOfBirth"
                                                                    required
                                                                    type="date"
                                                                    width="402px"
                                                                    min=${this.minDobDate}
                                                                    max=${new Date().toISOString().split('T')[0]}
                                                                    value=${this.formData.childrenDatesOfBirth[index] ||
                                                                    ''}
                                                                    default-value=${this.formData.childrenDatesOfBirth[
                                                                        index
                                                                    ] || ''}
                                                                    error-message=${this.getChildDobError(
                                                                        this.formData.childrenDatesOfBirth[index] || ''
                                                                    )}
                                                                    @lh-input=${(e: Event) => {
                                                                        const target = e.target as HTMLInputElement;
                                                                        const newDates = [
                                                                            ...this.formData.childrenDatesOfBirth,
                                                                        ];
                                                                        newDates[index] = target.value;
                                                                        this.formData = {
                                                                            ...this.formData,
                                                                            childrenDatesOfBirth: newDates,
                                                                        };
                                                                    }}
                                                                    >Child ${index + 1} date of birth</lh-input
                                                                >
                                                            </lh-grid-item>
                                                        </lh-grid>
                                                    `
                                                )}
                                            `
                                          : nothing}
                                  `
                                : nothing
                        }

                        <lh-divider></lh-divider>
                        <lh-typography size="lg" weight="700">Estimated subsidy</lh-typography>
                        <lh-grid gap="lg">
                            <lh-grid-item>
                                <lh-input
                                    name="householdIncome"
                                    width="402px"
                                    type="text"
                                    max-count="15"
                                    value=${this.formData.householdIncome}
                                    default-value=${this.formData.householdIncome}
                                    error-message=${this.validationErrors.householdIncomeError}
                                    @lh-input=${(e: Event) => this.onInputChange(e, 'householdIncome')}
                                    @lh-change=${(e: Event) => this.onInputChange(e, 'householdIncome')}
                                    >Household income</lh-input
                                >
                            </lh-grid-item>
                            <lh-grid-item>
                                <lh-input
                                    name="numberOfPeopleInHousehold"
                                    width="402px"
                                    type="number"
                                    min="0"
                                    value=${this.formData.numberOfPeopleInHousehold || ''}
                                    default-value=${this.formData.numberOfPeopleInHousehold || ''}
                                    @lh-input=${(e: Event) => this.onInputChange(e, 'numberOfPeopleInHousehold')}
                                    >Number of people in household</lh-input
                                >
                            </lh-grid-item>
                        </lh-grid>
                        <lh-divider></lh-divider>
                        <lh-button-group alignment="right" size="lg">
                            <lh-button variant="text" ?disabled=${this.isContinueDisabled} @click=${this.onCancel}
                                >Cancel</lh-button
                            >
                            <lh-button
                                variant="filled"
                                class=${classMap({ 'visually-disabled': this.isContinueDisabled })}
                                ?loading=${this.loading}
                                ?disabled=${this.isContinueDisabled}
                                .disabled=${this.isContinueDisabled}
                                @click=${this.onContinue}
                                >Continue</lh-button
                            >
                        </lh-button-group>
                    </lh-container>
                </div>
                </lh-container>
                ${this.loading ? html` <div class="overlay"><lh-loader></lh-loader></div> ` : nothing}
            </lh-container>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-applicant-information': ApplicantInformationPage;
    }
}
